import axios from 'axios'
import * as Sentry from '@sentry/react'
import { log } from './telemetry'
import { apiErrorCode, moduleNotInPackageMessage } from './apiError'

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api',
  timeout: 30_000,
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error)) {
      Sentry.addBreadcrumb({
        category: 'api',
        message: `HTTP ${error.config?.method?.toUpperCase()} ${error.config?.url} failed: ${error.message}`,
        level: 'error',
        data: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          responseData: error.response?.data,
        },
      })
      log.error('API request failed', {
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        status: error.response?.status,
      })
    }
    return Promise.reject(error)
  }
)

let tokenResolver: (() => Promise<string | null>) | null = null

export function registerTokenResolver(resolver: () => Promise<string | null>) {
  tokenResolver = resolver
}

export function setAuthToken(token: string) {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`
}

/**
 * The Playwright bypass token from localStorage, or null.
 *
 * The only place that reads E2E_TOKEN — api.ts, App.tsx and AuthContext.tsx all
 * go through here. localStorage is readable by any script on the origin, so a
 * build that accepts a bearer token from it is a build anything on the page can
 * sign in as. Behind VITE_E2E so that can be turned off for the shipped image.
 *
 * The flag defaults to on: the Playwright suite (tests/e2e/support/fixtures.ts)
 * injects the token this way, and backoffice-dev is built without VITE_E2E set.
 * Flipping the default needs the prod image built with VITE_E2E=0 first.
 */
export function e2eToken(): string | null {
  if ((import.meta.env.VITE_E2E ?? '1') !== '1') return null
  try {
    return localStorage.getItem('E2E_TOKEN')
  } catch {
    return null
  }
}

/**
 * The bearer token for the next request, or null when there is none.
 *
 * The e2e bypass token wins; otherwise the Clerk resolver AuthContext registered.
 * A resolver that throws is logged and counts as no token. The axios interceptor
 * below and the live-events stream (hooks/useLiveEvents) both ask here, so the
 * order lives in one place.
 */
export async function resolveAuthToken(): Promise<string | null> {
  const bypassToken = e2eToken()
  if (bypassToken) return bypassToken

  if (!tokenResolver) return null
  try {
    return (await tokenResolver()) || null
  } catch (e) {
    console.error('Failed to resolve auth token dynamically', e)
    return null
  }
}

api.interceptors.request.use(async (config) => {
  const token = await resolveAuthToken()
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
})

export class ApiError extends Error {
  status?: number;
  /** The body's machine code, e.g. `module_not_in_package` or `package_limit`. */
  code?: string;
  constructor(message: string, status?: number, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

/** The ApiError for a failed request: the server's message, or "Not in your package" for a module that's off. */
function toApiError(error: any): ApiError {
  const code = apiErrorCode(error) ?? undefined
  const message = code === 'module_not_in_package'
    ? moduleNotInPackageMessage(error)
    : error.response?.data?.message || error.message || 'API request failed'
  return new ApiError(message, error.response?.status, code)
}

const cleanUrl = (url: string) => {
  if (url.startsWith('/api/')) {
    return url.substring(4) // Strip '/api' prefix so Axios prepends it correctly
  }
  return url
}

export async function apiGet<T>(url: string): Promise<T> {
  try {
    const res = await api.get<T>(cleanUrl(url))
    return res.data
  } catch (error: any) {
    throw toApiError(error)
  }
}

export async function apiPost<T, D = any>(url: string, data?: D): Promise<T> {
  try {
    const res = await api.post<T>(cleanUrl(url), data)
    return res.data
  } catch (error: any) {
    throw toApiError(error)
  }
}

export async function apiPut<T, D = any>(url: string, data?: D): Promise<T> {
  try {
    const res = await api.put<T>(cleanUrl(url), data)
    return res.data
  } catch (error: any) {
    throw toApiError(error)
  }
}
